import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Button, Form, Modal, message } from "antd";
import { QRCodeSVG } from "qrcode.react";
import { serverFetch, strDate2Server } from "@extensions";
import { useCompany } from "@redux";
import ProfileFields from "./profile-fields";
import { useGetIsPhone } from "@extensions";
import "./child-modal.css";

const errMsg = (e) => e?.userMessage || e?.UserMessage || "Ошибка запроса";

/**
 * Модалка создания и редактирования участника группы.
 * Поля анкеты берём из общего компонента, чтобы вид совпадал со страницей профиля.
 * child === null - создание нового участника группы, иначе - редактирование существующего.
 */
const ChildModal = ({ open, child, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);

  const handleSaved = (values) => {
    setSaving(true);
    // Телефон и почту не отправляем: для участника группы их задаёт администратор
    const saveData = {
      name: (values?.name || "").trim(),
      dateBirth: strDate2Server(values?.dateBirth),
      sex: values?.sex ?? null,
    };
    const request = child
      ? serverFetch(`OnlineClient/FamilyUpdate/${child.id}`, {
        method: "PUT",
        bodyData: saveData,
      })
      : serverFetch("OnlineClient/FamilyAdd", {
        method: "POST",
        bodyData: saveData,
      });
    return request
      .then(() => {
        message.success(child ? "Данные участника группы сохранены" : "Участник группы добавлен");
        if (onSaved) onSaved(saveData);
      })
      .catch((e) => {
        message.error(errMsg(e));
      })
      .finally(() => {
        setSaving(false);
      });
  };

  return (
    <Modal
      title={clientTitle(child)}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={560}
      className="child-modal"
    >
      <ProfileFieldsForm
        key={child ? `child-${child.id}` : "child-new"}
        child={child}
        saving={saving}
        onCancel={onClose}
        onSaved={handleSaved}
      />
    </Modal>
  );
};

// Заголовок модалки зависит от режима: создание или редактирование
const clientTitle = (child) => (child ? "Редактирование участника группы" : "Добавление участника группы");

const ProfileFieldsForm = ({ child, saving, onCancel, onSaved }) => {
  const [form] = Form.useForm();
  const company = useCompany();
  const isPhone = useGetIsPhone();
  // Модалка увеличенного QR: на телефоне код плохо разглядеть,
  // поэтому тап открывает его во всю ширину экрана
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);

  const openQrModal = () => {
    if (child?.card) {
      setIsQrModalOpen(true);
    }
  };

  const closeQrModal = () => setIsQrModalOpen(false);

  // Доступность с клавиатуры: Enter и пробел открывают увеличение, как клик
  const onQrKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openQrModal();
    }
  };

  // Форма сама подставляет значения, поэтому данные надо записать в её хранилище
  useEffect(() => {
    form.setFieldsValue({
      name: child?.name || "",
      dateBirth: child?.dateBirth ? dayjs(child.dateBirth) : null,
      sex: childSexOrDefault(child),
      email: child?.email || "",
      phone: child?.phone || "",
    });
  }, [child, form]);

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        onSaved(values);
      })
      .catch(() => {
        // Ошибки валидации antd - форма сама покажет незаполненные поля
      });
  };

  return (
    <div className="account-form account-form-root">
      <Form
        colon={false}
        form={form}
        layout={isPhone ? "vertical" : "horizontal"}
        labelCol={isPhone ? undefined : { span: 7 }}
        wrapperCol={isPhone ? undefined : { span: 17 }}
        onFinish={onSubmit}
      >
        <div className="account-content-wrapper">
          <div className="account-form-inner">
            <ProfileFields
              company={company}
              phone={child?.phone || ""}
              emailEditable={false}
            />
            <Form.Item label=" ">
              <div className="account-modal-buttons">
                <Button
                  className="btn-blue-raund"
                  size="middle"
                  loading={saving}
                  htmlType="submit"
                >
                  {child ? "Сохранить" : "Добавить"}
                </Button>
                <Button onClick={onCancel} className="account-modal-cancel">
                  Отмена
                </Button>
              </div>
            </Form.Item>
            {child?.card && (
              <div
                className={`${isPhone ? "account-qr-code" : "child-modal-qr"}${isPhone ? " account-qr-code_clickable" : ""}`}
                role={isPhone ? "button" : undefined}
                tabIndex={isPhone ? 0 : undefined}
                onClick={isPhone ? openQrModal : undefined}
                onKeyDown={isPhone ? onQrKeyDown : undefined}
              >
                <QRCodeSVG value={child.card} size={isPhone ? 150 : 120} level="H" />
              </div>
            )}
          </div>
        </div>
      </Form>
      {isPhone && child?.card && (
        <Modal
          open={isQrModalOpen}
          onCancel={closeQrModal}
          footer={null}
          centered
          width="calc(100vw - 24px)"
          wrapClassName="account-qr-modal"
          destroyOnClose
        >
          <div className="account-qr-modal-content">
            <QRCodeSVG value={child.card} size={320} level="H" />
          </div>
        </Modal>
      )}
    </div>
  );
};

// Пол участника группы: 0 - женщина, поэтому проверяем именно null/undefined
const childSexOrDefault = (child) => (child?.sex === null || child?.sex === undefined ? null : child.sex);

export default ChildModal;
